/*
 * cloudToggleLegacy — Cloud↔Lokal Umschalt-Logik fuer das Project Dashboard.
 *
 * Aus der alten ProjectDashboard-Klasse extrahiert (Phase 4 Stufe B). In
 * Stufe I (Spec §10.1) wurde der Off-Pfad um einen UUID-Swap ergaenzt:
 * nach dem Server-Loeschen bekommt das Projekt lokal eine neue UUID, damit
 * ein parallel laufendes Cloud-Geraet das Projekt nicht ueber denselben
 * Schluessel wieder hochpushen kann.
 *
 * Signatur: toggleCloudForProject({ project, projectManager, syncService, tabManager, dialogManager })
 *   - Wenn cloudEnabled=false: aktiviert Cloud, laedt alle Paragraphen hoch.
 *   - Wenn cloudEnabled=true: zeigt Confirm, loescht Server-Daten, vergibt
 *     lokal eine neue UUID + haengt Paragraphen, Tabs und ProjectManager-
 *     Referenzen darauf um.
 */

import { generateUUID } from '../persistence/uuid.js'

export async function toggleCloudForProject({ project, projectManager, syncService, tabManager, dialogManager }) {
    if (!syncService) return

    if (!project.cloudEnabled) {
        return _enableCloud({ project, projectManager, syncService, dialogManager })
    }
    return _disableCloud({ project, projectManager, syncService, tabManager, dialogManager })
}

/**
 * Cloud-Sync fuer ein frisch angelegtes Projekt aktivieren — still.
 * Neue Projekte sind cloud-first (Testphasen-Entscheidung 2026-07-02):
 * schlaegt der Upload fehl (offline, Server down), bleibt das Projekt
 * einfach lokal und traegt im Dashboard die "local"-Markierung — kein
 * Dialog, kein Retry. Der User kann Cloud dort jederzeit manuell aktivieren.
 */
export async function enableCloudForNewProject({ project, projectManager, syncService }) {
    if (!syncService || !project || project.cloudEnabled) return
    return _enableCloud({ project, projectManager, syncService, dialogManager: null })
}

async function _enableCloud({ project, projectManager, syncService, dialogManager }) {
    // Pending dirty-Writes draenen, BEVOR cloudEnabled=true gesetzt wird.
    // Sonst wuerde der naechste Flush alle dirty-Paragraphen ins Sync-
    // Pending enqueuen — der Batch-Upload hat sie aber schon direkt hochgeladen.
    if (project.id === projectManager.currentProjectId) {
        await projectManager.flushNow()
    }

    project.cloudEnabled = true
    project.syncStatus = 'pending'
    project.updatedAt = new Date().toISOString()
    await projectManager.putProject(project)

    // Snapshot der Pending-Eintraege — wird am Upload-Ende selektiv konsumiert.
    const pendingSnapshot = syncService.snapshotPending(project.id)

    let serverProjectCreated = false
    try {
        const paragraphs = await projectManager.getParagraphsByProject(project.id)
        const createRes = await fetch('/api/projects', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: project.id,
                title: project.title,
                author: project.author,
                paragraphOrder: project.paragraphOrder || [],
                // Folder-Zuordnung mit anlegen — sonst speichert der Server
                // folder_id=NULL und der naechste initialSync ueberschreibt
                // die lokal korrekte folderId mit null (Projekt rutscht aus
                // seinem Ordner in den Root).
                folderId: project.folderId || null
            })
        })
        if (!createRes.ok) {
            throw new Error(`Server-Anlage fehlgeschlagen: ${createRes.status}`)
        }
        serverProjectCreated = true

        if (paragraphs.length > 0) {
            const MAX_BATCH_BYTES = 1_500_000
            const MAX_BATCH_ITEMS = 500
            const allChanges = paragraphs.map(p => ({
                type: 'upsert',
                id: p.id,
                data: p.data,
                version: p.version || 1
            }))

            const changeSizes = allChanges.map(c => JSON.stringify(c).length)

            let offset = 0
            let batchIndex = 0
            while (offset < allChanges.length) {
                let end = offset
                let bytes = 0
                while (
                    end < allChanges.length &&
                    end - offset < MAX_BATCH_ITEMS &&
                    (bytes === 0 || bytes + changeSizes[end] <= MAX_BATCH_BYTES)
                ) {
                    bytes += changeSizes[end]
                    end++
                }
                const batch = allChanges.slice(offset, end)
                const isLastBatch = end >= allChanges.length

                const res = await fetch('/api/sync', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        projectId: project.id,
                        projectVersion: project.version || 1,
                        lastSyncTime: '0',
                        cursorState: isLastBatch ? project.cursorState : undefined,
                        changes: batch,
                        paragraphOrder: isLastBatch ? project.paragraphOrder : null
                    })
                })

                if (!res.ok) {
                    const details = await res.text().catch(() => '')
                    throw new Error(`Batch ${batchIndex} (${batch.length} items, ${Math.round(bytes / 1024)} KB): ${res.status} ${details}`)
                }

                offset = end
                batchIndex++
            }
        }

        project.syncStatus = 'synced'
        await projectManager.putProject(project)
        syncService.removePending(project.id, pendingSnapshot)
    } catch (err) {
        console.warn('Cloud-Aktivierung fehlgeschlagen:', err.message)
        if (serverProjectCreated) {
            try {
                await fetch(`/api/projects/${project.id}/permanent`, { method: 'DELETE' })
            } catch { /* best effort */ }
        }
        project.cloudEnabled = false
        project.syncStatus = 'local'
        await projectManager.putProject(project)
        // Sync-State-Cleanup: Wir hatten cloudEnabled kurz auf true, dadurch
        // konnte enqueueParagraphs Pending-Eintraege fuer dieses Projekt
        // anlegen. Da der Upload fehlgeschlagen ist und das Projekt wieder
        // local-only ist, sind diese Pendings irrelevant — der naechste
        // Cloud-Toggle-Versuch sweept ohnehin alle Paragraphen direkt aus
        // dem Store hoch. clearPending verhindert, dass die alten Pendings
        // beim spaeteren erfolgreichen Cloud-Toggle einen zweiten,
        // redundanten Sync-Lauf ausloesen.
        if (typeof syncService.clearPending === 'function') {
            syncService.clearPending(project.id)
        }
        if (dialogManager) {
            await dialogManager.showInfo(
                'Cloud activation failed',
                err.message,
                'Project is still available locally.'
            )
        }
    }
}

async function _disableCloud({ project, projectManager, syncService, tabManager, dialogManager }) {
    const confirmed = await dialogManager.showConfirm(
        'Disable cloud',
        'Server data will be deleted.',
        'A new local-only copy stays on this device.'
    )
    if (!confirmed) return

    const store = projectManager._store
    const oldId = project.id
    const newId = generateUUID()

    // 1) Server-Löschung. Bei offline / Fehler trotzdem lokal fortsetzen —
    //    der Server-Tombstone (falls vorhanden) regelt das beim naechsten
    //    Geraet eh, und das UUID-Swap-Argument der Spec §10.1 gilt
    //    unabhaengig.
    try {
        await fetch(`/api/projects/${oldId}/permanent`, { method: 'DELETE' })
    } catch { /* best effort */ }

    // 2) SyncService: keine pending-Pushes mehr fuer die alte UUID. Muss
    //    VOR den Store-Mutationen passieren, sonst koennte ein Timer
    //    feuern wahrend die Paragraphen umgehaengt werden.
    if (typeof syncService.discardProject === 'function') {
        syncService.discardProject(oldId)
    }

    // 3) Paragraphen umhaengen: selbe Paragraph-UUIDs, nur projectId neu.
    //    putParagraphs ueberschreibt die existierenden Eintraege per
    //    Paragraph-ID — beim spaeteren store.deleteProject(oldId) findet
    //    der Cascade-Cursor keine Eintraege mit alter projectId mehr.
    const paragraphs = await store.getParagraphsByProject(oldId)
    if (paragraphs.length > 0) {
        const updated = paragraphs.map(p => ({ ...p, projectId: newId }))
        await store.putParagraphs(updated)
    }

    // 4) Neues Projekt schreiben (UUID neu, cloudEnabled aus, version=1).
    const newProject = {
        ...project,
        id: newId,
        cloudEnabled: false,
        syncStatus: 'local',
        version: 1,
        lastSyncTime: undefined,
        updatedAt: new Date().toISOString()
    }
    delete newProject.lastSyncTime
    await store.putProject(newProject)

    // 5) Altes Projekt loeschen. Cascade entfernt nur noch die alte
    //    Project-Row — die Paragraphen sind ja schon umgehaengt.
    await store.deleteProject(oldId)

    // 6) In-memory References umhaengen.
    projectManager.swapProjectId(oldId, newId, newProject)
    if (tabManager && typeof tabManager.replaceTab === 'function') {
        tabManager.replaceTab(oldId, newId)
    }
    if (typeof syncService.setActiveProject === 'function'
            && projectManager.currentProjectId === newId) {
        syncService.setActiveProject(newId)
    }
}
